(() => {
  const lineColors = {
    "ichiban-kuji": "#a63d40",
    figma: "#26736b",
    nendoroid: "#446b9e",
    "sh-figuarts": "#8a5526",
    "pop-up-parade": "#75528a",
    "scale-figure": "#7a3e63",
    "prize-figure": "#536a2e",
    other: "#52606d"
  };

  const productLineNames = {
    "ichiban-kuji": "Ichiban Kuji",
    figma: "Figma",
    nendoroid: "Nendoroid",
    "sh-figuarts": "S.H.Figuarts",
    "pop-up-parade": "POP UP PARADE",
    "scale-figure": "Scale Figure",
    "prize-figure": "Prize Figure"
  };

  const numericId = (gid) => String(gid || "").split("/").pop();
  const currencyFormatter = (currency, locale) => new Intl.NumberFormat(locale || "en", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 2
  });

  const localizedStorefrontUrl = (value) => {
    if (!value) return value;
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return value;
      const root = String(window.Shopify?.routes?.root || "/").replace(/\/$/, "");
      const path = url.pathname.replace(/^\/(?:ko|id|th|vi|zh-hans|zh-hant)(?=\/|$)/, "");
      return `${root}${path || "/"}${url.search}${url.hash}`.replace(/^\/\//, "/");
    } catch (_error) {
      return value;
    }
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };

  const formatTemplate = (template, values) => Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`%${key}%`, String(value)),
    template
  );

  async function refreshCartCount() {
    try {
      const rootUrl = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${rootUrl}cart.js`, {
        headers: { Accept: "application/json" }
      });
      if (!response.ok) return;
      const cart = await response.json();
      const count = Number(cart.item_count || 0);
      document.querySelectorAll("[data-japotyle-cart-link]").forEach((link) => {
        const badge = link.querySelector("[data-japotyle-cart-count]");
        if (badge) {
          badge.textContent = String(count);
          badge.hidden = count <= 0;
        }
        const labelTemplate = link.dataset.cartLabelTemplate || "Cart (%COUNT%)";
        link.setAttribute("aria-label", labelTemplate.replace("%COUNT%", String(count)));
      });
    } catch (_error) {
      // The item was added successfully even if the optional count refresh failed.
    }
  }

  async function addItemsToCart(items, button, labels, errorNode = null) {
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    if (errorNode) errorNode.hidden = true;
    try {
      const postItems = async (cartItems) => {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: cartItems })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.description || labels.error || "Unable to add this item.");
        }
        return payload;
      };

      const cartItems = items.map((item) => ({
        ...item,
        properties: item.properties ? { ...item.properties } : undefined
      }));
      const childIndex = cartItems.findIndex((item) => item.parent_id != null);
      const child = childIndex >= 0 ? cartItems[childIndex] : null;
      const parentIndex = child
        ? cartItems.findIndex((item, index) => index !== childIndex && String(item.id) === String(child.parent_id))
        : -1;

      if (child && parentIndex >= 0) {
        const bundleId = window.crypto?.randomUUID?.() || `photo-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const parent = cartItems[parentIndex];
        parent.properties = { ...(parent.properties || {}), _photo_bundle_id: bundleId };
        child.properties = { ...(child.properties || {}), _photo_bundle_id: bundleId };
        await postItems([parent, child]);
      } else {
        await postItems(cartItems);
      }
      await refreshCartCount();
      button.textContent = labels.added;
      button.removeAttribute("aria-busy");
      document.dispatchEvent(new CustomEvent("japotyle:cart-updated"));
      return true;
    } catch (error) {
      button.disabled = false;
      button.removeAttribute("aria-busy");
      button.textContent = labels.add;
      if (errorNode) {
        errorNode.textContent = error.message || labels.error;
        errorNode.hidden = false;
      }
      return false;
    }
  }

  const isUsedVariant = (variant) => {
    const condition = String(variant?.condition || variant?.title || "").toLowerCase();
    const sku = String(variant?.sku || "").toLowerCase();
    return condition === "used" || sku.endsWith("-used");
  };

  function makePhotoOption(photo) {
    const wrapper = element("div", "japotyle-card-photo-option");
    wrapper.hidden = true;

    const choice = element("label", "japotyle-card-photo-option__choice");
    const checkbox = element("input");
    checkbox.type = "checkbox";
    checkbox.disabled = true;
    checkbox.dataset.japotyleCardPhotoCheckbox = "";
    choice.append(
      checkbox,
      element("span", "japotyle-card-photo-option__check"),
      element("span", "", photo.title)
    );

    const help = element("span", "japotyle-photo-help");
    help.tabIndex = 0;
    help.setAttribute("aria-label", photo.title);
    const icon = element("span", "japotyle-photo-help__icon", "i");
    icon.setAttribute("aria-hidden", "true");
    const tooltip = element("span", "japotyle-photo-help__tooltip");
    tooltip.setAttribute("role", "tooltip");
    const link = element("a", "", photo.linkLabel);
    link.href = photo.pageUrl;
    tooltip.append(
      element("span", "", photo.tooltip),
      element("strong", "", photo.priceLabel),
      link
    );
    help.append(icon, tooltip);
    wrapper.append(choice, help);
    return { wrapper, checkbox };
  }

  function bindLiquidCards(root = document) {
    root.querySelectorAll("[data-japotyle-liquid-card]").forEach((card) => {
      if (card.dataset.bound === "true") return;
      card.dataset.bound = "true";
      const form = card.querySelector("[data-japotyle-card-form]");
      const input = card.querySelector("[data-japotyle-card-variant-input]");
      const price = card.querySelector("[data-japotyle-card-price]");
      const add = card.querySelector("[data-japotyle-card-add]");
      const photoOption = card.querySelector("[data-japotyle-card-photo]");
      const photoCheckbox = card.querySelector("[data-japotyle-card-photo-checkbox]");
      const error = card.querySelector("[data-japotyle-card-error]");

      const syncPhotoOption = () => {
        if (!photoOption || !photoCheckbox || !input) return;
        const canRequestPhotos = input.dataset.isUsed === "true"
          && input.dataset.available === "true"
          && Boolean(card.dataset.serviceVariantId);
        photoOption.hidden = !canRequestPhotos;
        photoCheckbox.disabled = !canRequestPhotos;
        if (!canRequestPhotos) photoCheckbox.checked = false;
      };

      card.querySelectorAll("[data-japotyle-variant]").forEach((option) => {
        option.addEventListener("click", () => {
          if (option.disabled || option.dataset.variantAvailable !== "true") return;
          card.querySelectorAll("[data-japotyle-variant]").forEach((item) => {
            item.classList.toggle("is-selected", item === option);
          });
          if (input) {
            input.value = option.dataset.variantId;
            input.dataset.isUsed = option.dataset.variantIsUsed;
            input.dataset.sku = option.dataset.variantSku || "";
            input.dataset.available = option.dataset.variantAvailable;
          }
          if (price) price.textContent = option.dataset.variantPrice;
          if (add) {
            const available = option.dataset.variantAvailable === "true";
            add.disabled = !available;
            add.textContent = available
              ? card.dataset.addLabel || "Add to cart"
              : card.dataset.unavailableLabel || "Currently unavailable";
          }
          if (error) error.hidden = true;
          syncPhotoOption();
        });
      });
      if (form && add) {
        form.addEventListener("submit", async (event) => {
          event.preventDefault();
          const requested = Boolean(photoCheckbox?.checked) && input.dataset.isUsed === "true";
          const productProperties = requested ? {
            [card.dataset.photoProperty]: card.dataset.photoPropertyValue,
            _photo_approval_required: "true",
            _photo_service_sku: card.dataset.serviceSku
          } : undefined;
          const items = [{
            id: Number(numericId(input.value)),
            quantity: 1,
            ...(productProperties ? { properties: productProperties } : {})
          }];
          if (requested) {
            const linkedItem = [card.dataset.productTitle, input.dataset.sku].filter(Boolean).join(" · ");
            items.push({
              id: Number(numericId(card.dataset.serviceVariantId)),
              quantity: 1,
              parent_id: Number(numericId(input.value)),
              properties: {
                [card.dataset.forItemProperty]: linkedItem,
                SKU: input.dataset.sku || "",
                _photo_approval_service: "true",
                _linked_variant_id: input.value,
                _linked_sku: input.dataset.sku || ""
              }
            });
          }
          await addItemsToCart(items, add, {
            add: card.dataset.addLabel || add.textContent,
            added: card.dataset.addedLabel || "Added",
            error: card.dataset.errorLabel
          }, error);
        });
      }
      syncPhotoOption();
    });
  }

  function makeApiCard(item, context) {
    const card = element("article", "japotyle-figure-card");
    card.style.setProperty("--japotyle-line-color", lineColors[item.product_line_id] || lineColors.other);
    const favorite = element("button", "japotyle-favorite-button");
    favorite.type = "button";
    favorite.dataset.japotyleFavorite = "";
    favorite.dataset.productId = item.shopify_product_id || item.product_id || "";
    favorite.dataset.productHandle = item.handle || "";
    favorite.dataset.productTitle = item.title || "";
    favorite.dataset.productUrl = item.product_url || "";
    favorite.dataset.productImage = item.image_url || "";
    favorite.setAttribute("aria-pressed", "false");
    favorite.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 21s-7.2-4.35-9.5-8.45C.45 8.9 2.15 4.5 6.35 4.05 8.7 3.8 10.45 5 12 6.85 13.55 5 15.3 3.8 17.65 4.05c4.2.45 5.9 4.85 3.85 8.5C19.2 16.65 12 21 12 21Z"></path></svg>';
    const media = element("a", "japotyle-figure-card__media");
    media.href = localizedStorefrontUrl(item.product_url || `/products/${item.handle}`);
    if (item.image_url) {
      const image = element("img");
      image.src = item.image_url;
      image.alt = item.title;
      image.loading = "lazy";
      media.append(image);
    }
    const badges = element("span", "japotyle-figure-card__badges");
    if (item.complete_set) {
      badges.append(element("span", "japotyle-card-badge japotyle-card-badge--set", context.labels.completeSet));
    }
    if (item.availability === "sold_out") {
      badges.append(element("span", "japotyle-card-badge japotyle-card-badge--sold", context.labels.unavailable));
    }
    media.append(badges);

    const body = element("div", "japotyle-figure-card__body");
    body.append(element("span", "japotyle-figure-card__line", item.product_line_name || item.vendor));
    const title = element("a", "japotyle-figure-card__title", item.title);
    title.href = media.href;
    body.append(title);
    if (item.work_name) body.append(element("span", "japotyle-figure-card__work", item.work_name));
    if (item.character_names?.length) {
      body.append(element("span", "japotyle-figure-card__characters", item.character_names.slice(0, 2).join(", ")));
    }

    const variants = (item.condition_data || []).filter((variant) => variant.shopify_variant_id);
    let selected = variants.find((variant) => Number(variant.quantity || 0) > 0) || variants[0];
    const purchase = element("div", "japotyle-figure-card__purchase");
    const price = element("strong", "", selected?.price != null
      ? context.money.format(Number(selected.price))
      : context.money.format(Number(item.min_price_usd || 0)));
    favorite.dataset.productPrice = price.textContent;
    let action = null;

    const updatePurchase = (variant) => {
      selected = variant;
      price.textContent = context.money.format(Number(variant.price || 0));
      favorite.dataset.productPrice = price.textContent;
      const available = Number(variant.quantity || 0) > 0;
      action.disabled = !available;
      action.className = "japotyle-card-add";
      action.textContent = available ? context.labels.add : context.labels.unavailable;
      const canRequestPhotos = available && isUsedVariant(variant) && Boolean(context.photo.serviceVariantId);
      photoOption.wrapper.hidden = !canRequestPhotos;
      photoOption.checkbox.disabled = !canRequestPhotos;
      if (!canRequestPhotos) photoOption.checkbox.checked = false;
    };

    const switcher = element("div", "japotyle-condition-switch");
    [
      { name: "New", label: context.labels.newCondition },
      { name: "Used", label: context.labels.usedCondition }
    ].forEach((condition) => {
      const variant = variants.find((candidate) => (
        candidate.condition || candidate.title || ""
      ).toLowerCase() === condition.name.toLowerCase());
      const available = Boolean(variant) && Number(variant.quantity || 0) > 0;
      const option = element(
        "button",
        `japotyle-condition-switch__option${variant && variant === selected ? " is-selected" : ""}${available ? "" : " is-unavailable"}`,
        condition.label
      );
      option.type = "button";
      option.disabled = !available;
      option.setAttribute("aria-disabled", String(!available));
      if (!available) {
        option.title = context.labels.unavailable;
      } else {
        option.addEventListener("click", () => {
          switcher.querySelectorAll("button").forEach((button) => button.classList.toggle("is-selected", button === option));
          updatePurchase(variant);
        });
      }
      switcher.append(option);
    });
    body.append(switcher);

    const photoOption = makePhotoOption(context.photo);
    body.append(photoOption.wrapper);

    action = element("button", "japotyle-card-add", context.labels.add);
    action.type = "button";
    action.addEventListener("click", async () => {
      if (Number(selected?.quantity || 0) > 0) {
        const requested = photoOption.checkbox.checked && isUsedVariant(selected);
        const productProperties = requested ? {
          [context.photo.property]: context.photo.propertyValue,
          _photo_approval_required: "true",
          _photo_service_sku: context.photo.serviceSku
        } : undefined;
        const items = [{
          id: Number(numericId(selected.shopify_variant_id)),
          quantity: 1,
          ...(productProperties ? { properties: productProperties } : {})
        }];
        if (requested) {
          const linkedItem = [item.title, selected.sku].filter(Boolean).join(" · ");
          items.push({
            id: Number(numericId(context.photo.serviceVariantId)),
            quantity: 1,
            parent_id: Number(numericId(selected.shopify_variant_id)),
            properties: {
              [context.photo.forItemProperty]: linkedItem,
              SKU: selected.sku || "",
              _photo_approval_service: "true",
              _linked_variant_id: numericId(selected.shopify_variant_id),
              _linked_sku: selected.sku || ""
            }
          });
        }
        await addItemsToCart(items, action, context.labels);
      }
    });
    if (selected) {
      updatePurchase(selected);
    } else {
      action.disabled = true;
      action.textContent = context.labels.unavailable;
    }
    purchase.append(price, action);
    body.append(purchase);
    const details = element("a", "japotyle-figure-card__details", context.labels.details);
    details.href = media.href;
    body.append(details);
    card.append(favorite, media, body);
    window.JapotyleFavorites?.refresh();
    return card;
  }

  function makeSeriesCard(series, context) {
    const card = element("a", "japotyle-series-search-card");
    card.href = localizedStorefrontUrl(series.url || `/collections/${series.collection_handle || series.handle}`);
    const media = element("span", "japotyle-series-search-card__media");
    if (series.image_url) {
      const image = element("img");
      image.src = series.image_url;
      image.alt = series.title;
      image.loading = "lazy";
      media.append(image);
    }
    const body = element("span", "japotyle-series-search-card__body");
    const productLineName = productLineNames[series.product_line_id]
      || String(series.product_line_id || "").replaceAll("-", " ");
    const seriesLabel = [productLineName, context.labels.series].filter(Boolean).join(" ");
    body.append(
      element("small", "", seriesLabel),
      element("strong", "", series.title),
      element("span", "", `${series.product_count} ${context.labels.items}`)
    );
    card.append(media, body);
    return card;
  }

  function renderSeries(root, payload, context) {
    const section = root.querySelector("[data-japotyle-series-results]");
    const grid = root.querySelector("[data-japotyle-series-grid]");
    if (!section || !grid) return;
    grid.replaceChildren();
    (payload.series_results || []).forEach((series) => grid.append(makeSeriesCard(series, context)));
    section.hidden = grid.childElementCount === 0;
  }

  function renderGroups(root, payload, context) {
    const groupsContainer = root.querySelector("[data-japotyle-search-groups]");
    const groupIndex = root.querySelector("[data-japotyle-group-index]");
    groupsContainer.replaceChildren();
    groupIndex.replaceChildren();
    payload.groups.forEach((group) => {
      const groupId = `japotyle-group-${group.id}`;
      const indexLink = element("a", "", `${group.label} ${group.count}`);
      indexLink.href = `#${groupId}`;
      indexLink.style.setProperty("--japotyle-group-color", lineColors[group.id] || lineColors.other);
      groupIndex.append(indexLink);

      const section = element("section", "japotyle-search-group");
      section.id = groupId;
      section.dataset.groupId = group.id;
      section.style.setProperty("--japotyle-group-color", lineColors[group.id] || lineColors.other);
      const head = element("header", "japotyle-search-group__head");
      head.append(element("h2", "", group.label), element("span", "", `${group.count} ${context.labels.items}`));
      const grid = element("div", "japotyle-figure-grid");
      group.items.forEach((item) => grid.append(makeApiCard(item, context)));
      section.append(head, grid);
      if (group.has_more) {
        const more = element("button", "japotyle-search-group__more", context.labels.more);
        more.type = "button";
        more.dataset.nextPage = group.next_page;
        more.addEventListener("click", async () => {
          more.disabled = true;
          const nextPayload = await fetchSearch(root, Number(more.dataset.nextPage));
          const nextGroup = nextPayload.groups.find((candidate) => candidate.id === group.id);
          nextGroup?.items.forEach((item) => grid.append(makeApiCard(item, context)));
          if (nextGroup?.has_more) {
            more.dataset.nextPage = nextGroup.next_page;
            more.disabled = false;
          } else {
            more.remove();
          }
        });
        section.append(more);
      }
      groupsContainer.append(section);
    });
    groupIndex.hidden = payload.groups.length === 0;
  }

  async function fetchSearch(root, page = 1) {
    const params = new URLSearchParams({
      q: root.querySelector("[data-japotyle-search-form] input[name='q']").value,
      locale: root.dataset.locale || "en",
      country: root.dataset.country || "",
      group_by: root.querySelector("[data-japotyle-group-by]").value,
      sort: root.querySelector("[data-japotyle-sort]").value,
      page: String(page),
      per_group: "8"
    });
    root.querySelectorAll("[data-japotyle-filter]").forEach((control) => {
      if (control.value) params.set(control.dataset.japotyleFilter, control.value);
    });
    const response = await fetch(`${root.dataset.searchEndpoint}?${params.toString()}`, {
      headers: { Accept: "application/json" }
    });
    if (!response.ok) throw new Error("Search is temporarily unavailable.");
    const payload = await response.json();
    if (!payload.ok) throw new Error(payload.error || "Search is temporarily unavailable.");
    return payload;
  }

  async function enhanceSearch(root) {
    const status = root.querySelector("[data-japotyle-search-status]");
    const nativeResults = root.querySelector("[data-japotyle-native-results]");
    const count = root.querySelector("[data-japotyle-result-count]");
    const nativeProductCount = nativeResults?.querySelectorAll("[data-japotyle-liquid-card]").length || 0;
    const nativeCountText = count?.textContent || "";
    const context = {
      locale: root.dataset.locale,
      money: currencyFormatter(root.dataset.currency, root.dataset.locale),
      labels: {
        add: root.dataset.labelAdd,
        added: root.dataset.labelAdded,
        unavailable: root.dataset.labelUnavailable,
        details: root.dataset.labelDetails,
        more: root.dataset.labelLoadMore,
        photo: root.dataset.labelPhoto,
        completeSet: root.dataset.labelCompleteSet,
        items: root.dataset.labelItems,
        series: root.dataset.labelSeries,
        searching: root.dataset.labelSearching,
        resultsTemplate: root.dataset.labelResultsTemplate,
        groupLimitTemplate: root.dataset.labelGroupLimit,
        searchUnavailable: root.dataset.labelSearchUnavailable,
        newCondition: root.dataset.labelNew,
        usedCondition: root.dataset.labelUsed,
        error: root.dataset.photoError
      },
      photo: {
        serviceVariantId: root.dataset.serviceVariantId,
        serviceSku: root.dataset.serviceSku,
        title: root.dataset.photoTitle,
        tooltip: root.dataset.photoTooltip,
        linkLabel: root.dataset.photoLinkLabel,
        priceLabel: root.dataset.photoPriceLabel,
        pageUrl: root.dataset.photoPageUrl,
        property: root.dataset.photoProperty,
        propertyValue: root.dataset.photoPropertyValue,
        forItemProperty: root.dataset.forItemProperty
      }
    };
    const refresh = async () => {
      status.hidden = false;
      status.textContent = context.labels.searching;
      try {
        const payload = await fetchSearch(root);
        context.money = currencyFormatter(
          payload.currency || root.dataset.currency,
          root.dataset.locale
        );
        renderSeries(root, payload, context);
        renderGroups(root, payload, context);
        const hasApiResults = Number(payload.total_count || 0) > 0
          || (payload.series_results || []).length > 0;
        if (!hasApiResults && nativeProductCount > 0) {
          nativeResults.hidden = false;
          count.textContent = nativeCountText;
          status.hidden = true;
          return;
        }
        nativeResults.hidden = true;
        count.textContent = formatTemplate(context.labels.resultsTemplate, {
          count: payload.total_count,
          query: payload.query
        });
        if (payload.groups_truncated) {
          status.textContent = formatTemplate(context.labels.groupLimitTemplate, {
            shown: payload.groups.length,
            total: payload.total_group_count
          });
          status.hidden = false;
        } else {
          status.hidden = true;
        }
      } catch (error) {
        nativeResults.hidden = false;
        status.textContent = context.labels.searchUnavailable;
      }
    };
    root.querySelectorAll("[data-japotyle-group-by], [data-japotyle-sort], [data-japotyle-filter]").forEach((control) => {
      control.addEventListener("change", refresh);
    });
    root.querySelector("[data-japotyle-clear-filters]")?.addEventListener("click", () => {
      root.querySelectorAll("[data-japotyle-filter]").forEach((control) => { control.value = ""; });
      refresh();
    });
    root.querySelector("[data-japotyle-filter-toggle]")?.addEventListener("click", (event) => {
      const panel = root.querySelector("[data-japotyle-filter-panel]");
      const open = panel.classList.toggle("is-open");
      event.currentTarget.setAttribute("aria-expanded", String(open));
    });
    await refresh();
  }

  function bindCollectionFilters() {
    document.querySelectorAll("[data-japotyle-collection-search]").forEach((form) => {
      if (form.dataset.bound === "true") return;
      form.dataset.bound = "true";
  
      const root = form.closest(".japotyle-collection");
      const input = form.querySelector("[data-japotyle-collection-keyword]");
      const seriesOnly = form.querySelector("[data-japotyle-series-only]");
      const submit = form.querySelector("[data-japotyle-collection-submit]");
      const status = root?.querySelector("[data-japotyle-collection-status]");
      const nativeGrid = root?.querySelector("[data-japotyle-collection-native-grid]");
      const apiGrid = root?.querySelector("[data-japotyle-collection-api-grid]");
      const seriesGrid = root?.querySelector("[data-japotyle-collection-series-grid]");
      const pagination = root?.querySelector("[data-japotyle-collection-pagination]");
      const loadMore = root?.querySelector("[data-japotyle-collection-load-more]");
      const count = root?.querySelector("[data-japotyle-collection-count]");
      const sortSelect = root?.querySelector(".japotyle-collection-sort select");
      if (!root || !input || !seriesOnly || !nativeGrid || !apiGrid || !seriesGrid) return;
  
      const originalCount = count?.textContent || "";
      const locale = form.dataset.locale || "en";
      const context = {
        locale,
        money: currencyFormatter(form.dataset.currency || "USD", locale),
        labels: {
          add: form.dataset.labelAdd || "Add to cart",
          added: form.dataset.labelAdded || "Added",
          unavailable: form.dataset.labelUnavailable || "Currently unavailable",
          details: form.dataset.labelDetails || "View details",
          more: form.dataset.labelLoadMore || "Load more",
          completeSet: form.dataset.labelCompleteSet || "Complete Set",
          items: form.dataset.labelItems || "items",
          series: form.dataset.labelSeries || "Series",
          searching: form.dataset.labelSearching || "Searching…",
          searchUnavailable: form.dataset.labelSearchUnavailable || "Search is temporarily unavailable.",
          matchingItems: form.dataset.labelMatchingItems || "%count% matching items",
          matchingSeries: form.dataset.labelMatchingSeries || "%count% Series",
          productsMatching: form.dataset.labelProductsMatching || "Products matching “%query%” within %title%",
          noProductsMatching: form.dataset.labelNoProductsMatching || "No products matched “%query%” within %title%.",
          seriesMatching: form.dataset.labelSeriesMatching || "Series matching “%query%” within %title%",
          seriesBrowse: form.dataset.labelSeriesBrowse || "%title% Series",
          loading: form.dataset.labelLoading || "Loading…",
          newCondition: form.dataset.labelNew || "New",
          usedCondition: form.dataset.labelUsed || "Used",
          error: form.dataset.photoError || "Unable to add this item."
        },
        photo: {
          serviceVariantId: form.dataset.serviceVariantId || "",
          serviceSku: form.dataset.serviceSku || "",
          title: form.dataset.photoTitle || "Pre-Shipment Photo Approval",
          tooltip: form.dataset.photoTooltip || "",
          linkLabel: form.dataset.photoLinkLabel || "Learn more",
          priceLabel: form.dataset.photoPriceLabel || "",
          pageUrl: form.dataset.photoPageUrl || "/pages/pre-shipment-photo-approval",
          property: form.dataset.photoProperty || "Photo approval",
          propertyValue: form.dataset.photoPropertyValue || "Requested",
          forItemProperty: form.dataset.forItemProperty || "For item"
        }
      };
  
      let currentPage = 1;
  
      const apiSort = () => ({
        "price-ascending": "price_low",
        "price-descending": "price_high",
        "created-descending": "newest"
      }[sortSelect?.value] || "group_default");
  
      const addSharedParams = (params, mode) => {
        if (form.dataset.productLine) params.set("product_line", form.dataset.productLine);
        if (mode === "products" && form.dataset.collectionTag) {
          params.set("tag", form.dataset.collectionTag);
        }
        if (mode === "series" && form.dataset.scaleMaker) {
          params.set("scale_maker", form.dataset.scaleMaker);
        }
        const hasDedicatedScope = Boolean(form.dataset.collectionTag || form.dataset.scaleMaker);
        if (
          form.dataset.lineIndex !== "true"
          && !hasDedicatedScope
          && form.dataset.collectionHandle
        ) {
          params.set("series_collection", form.dataset.collectionHandle);
        }
      };
  
      const productParams = (page) => {
        const params = new URLSearchParams({
          q: input.value.trim(),
          locale,
          country: form.dataset.country || "",
          group_by: "product_line",
          sort: apiSort(),
          page: String(page),
          per_group: "24",
          max_groups: "1"
        });
        addSharedParams(params, "products");
  
        const availability = Array.from(
          form.querySelectorAll('input[name="filter.v.availability"]:checked')
        ).map((control) => control.value);
        if (availability.length === 1) {
          params.set("availability", availability[0] === "1" ? "in_stock" : "sold_out");
        }
        const minimum = form.querySelector('input[name="filter.v.price.gte"]')?.value;
        const maximum = form.querySelector('input[name="filter.v.price.lte"]')?.value;
        if (minimum) params.set("price_min", minimum);
        if (maximum) params.set("price_max", maximum);
        return params;
      };
  
      const seriesParams = (page) => {
        const params = new URLSearchParams({
          q: input.value.trim(),
          locale,
          page: String(page),
          per_page: "24"
        });
        addSharedParams(params, "series");
        return params;
      };
  
      const setBusy = (busy) => {
        if (!submit) return;
        submit.disabled = busy;
        submit.setAttribute("aria-busy", String(busy));
      };
  
      const reset = () => {
        currentPage = 1;
        nativeGrid.hidden = false;
        apiGrid.hidden = true;
        seriesGrid.hidden = true;
        apiGrid.replaceChildren();
        seriesGrid.replaceChildren();
        if (pagination) pagination.hidden = false;
        if (loadMore) loadMore.hidden = true;
        if (status) status.hidden = true;
        if (count) count.textContent = originalCount;
      };
  
      const showDynamic = (mode) => {
        nativeGrid.hidden = true;
        apiGrid.hidden = mode !== "products";
        seriesGrid.hidden = mode !== "series";
        if (pagination) pagination.hidden = true;
        if (loadMore) loadMore.hidden = true;
        if (status) {
          status.hidden = false;
          status.textContent = context.labels.searching;
        }
      };
  
      const updateLoadMore = (hasMore) => {
        if (!loadMore) return;
        loadMore.hidden = !hasMore;
        loadMore.disabled = false;
        loadMore.textContent = context.labels.more;
      };
  
      const renderProducts = async (page = 1, append = false) => {
        showDynamic("products");
        const response = await fetch(
          `${form.dataset.searchEndpoint}?${productParams(page).toString()}`,
          { headers: { Accept: "application/json" } }
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) {
          throw new Error(payload.error || "Search is temporarily unavailable.");
        }
        context.money = currencyFormatter(payload.currency || form.dataset.currency || "USD", locale);
        if (!append) apiGrid.replaceChildren();
        const groups = payload.groups || [];
        groups.forEach((group) => {
          (group.items || []).forEach((item) => apiGrid.append(makeApiCard(item, context)));
        });
        const query = input.value.trim();
        if (count) {
          count.textContent = formatTemplate(context.labels.matchingItems, {
            count: payload.total_count || 0
          });
        }
        if (status) {
          status.textContent = formatTemplate(
            payload.total_count ? context.labels.productsMatching : context.labels.noProductsMatching,
            { query, title: form.dataset.collectionTitle || "" }
          );
        }
        updateLoadMore(Boolean(groups[0]?.has_more));
        currentPage = page;
      };
  
      const renderSeries = async (page = 1, append = false) => {
        showDynamic("series");
        const response = await fetch(
          `${form.dataset.seriesEndpoint}?${seriesParams(page).toString()}`,
          { headers: { Accept: "application/json" } }
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) {
          throw new Error(payload.error || "Series search is temporarily unavailable.");
        }
        if (!append) seriesGrid.replaceChildren();
        (payload.items || []).forEach((series) => seriesGrid.append(makeSeriesCard(series, context)));
        if (count) {
          count.textContent = formatTemplate(context.labels.matchingSeries, {
            count: payload.total_count || 0
          });
        }
        if (status) {
          const query = input.value.trim();
          status.textContent = formatTemplate(
            query ? context.labels.seriesMatching : context.labels.seriesBrowse,
            { query, title: form.dataset.collectionTitle || "" }
          );
        }
        updateLoadMore(Boolean(payload.has_more));
        currentPage = page;
      };
  
      const run = async (page = 1, append = false) => {
        const query = input.value.trim();
        if (!query && !seriesOnly.checked) {
          reset();
          return;
        }
        setBusy(true);
        try {
          if (seriesOnly.checked) {
            await renderSeries(page, append);
          } else {
            await renderProducts(page, append);
          }
        } catch (error) {
          if (status) {
            status.hidden = false;
            status.textContent = error.message || context.labels.searchUnavailable;
          }
          updateLoadMore(false);
        } finally {
          setBusy(false);
        }
      };
  
      form.addEventListener("submit", (event) => {
        if (!input.value.trim() && !seriesOnly.checked) return;
        event.preventDefault();
        run(1, false);
      });
      seriesOnly.addEventListener("change", () => run(1, false));
      input.addEventListener("input", () => {
        if (!input.value.trim() && !seriesOnly.checked) reset();
      });
      loadMore?.addEventListener("click", () => {
        loadMore.disabled = true;
        loadMore.textContent = context.labels.loading;
        run(currentPage + 1, true);
      });
      if (sortSelect) {
        sortSelect.removeAttribute("onchange");
        sortSelect.addEventListener("change", () => {
          if (input.value.trim() || seriesOnly.checked) run(1, false);
          else sortSelect.form.submit();
        });
      }
  
      const url = new URL(window.location.href);
      const initialQuery = url.searchParams.get("collection_q") || "";
      const initialSeriesOnly = url.searchParams.get("series_only") === "1";
      if (initialQuery || initialSeriesOnly) {
        input.value = initialQuery;
        seriesOnly.checked = initialSeriesOnly;
        run(1, false);
      }
    });
  }

  function bindSeriesFilters() {
    document.querySelectorAll("[data-japotyle-series-filter-root]").forEach((root) => {
      const input = root.querySelector("[data-japotyle-series-filter]");
      const items = Array.from(root.querySelectorAll("[data-japotyle-series-filter-item]"));
      const empty = root.querySelector("[data-japotyle-series-filter-empty]");
      if (!input || !items.length) return;
      input.addEventListener("input", () => {
        const query = input.value.trim().toLocaleLowerCase();
        let visible = 0;
        items.forEach((item) => {
          const matches = !query || (item.dataset.seriesSearch || "").toLocaleLowerCase().includes(query);
          item.hidden = !matches;
          if (matches) visible += 1;
        });
        if (empty) empty.hidden = visible > 0;
      });
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    bindLiquidCards();
    bindSeriesFilters();
    bindCollectionFilters();
    document.querySelectorAll("[data-japotyle-catalog-search]").forEach((root) => {
      if (root.querySelector("[data-japotyle-group-by]")) enhanceSearch(root);
    });
  });
})();
